<div class="support-container">
  <div class="header">
    <button mat-icon-button (click)="router.navigate(['/admin/users'])" matTooltip="Back to Users">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h1>User Support</h1>
    <button mat-icon-button (click)="refresh()" [disabled]="isLoading || checkingVRChat || updatingVRChat" matTooltip="Refresh">
      <mat-icon>refresh</mat-icon>
    </button>
  </div>

  @if (isLoading && !user) {
    <div class="loading-container">
      <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
      <p>Loading user information...</p>
    </div>
  } @else if (!user) {
    <mat-card class="error-card">
      <mat-card-content>
        <p>User not found</p>
      </mat-card-content>
    </mat-card>
  } @else {
    <div class="content">
      <!-- Basic Information -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            @if (user.type === 'guest') {
              <em>Guest</em>
            }
            Basic Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Username:</span>
              <span class="value">{{ user.username }}</span>
            </div>
            <div class="info-item">
              <span class="label">User ID:</span>
              <span class="value">{{ user.id }}</span>
            </div>
            <div class="info-item">
              <span class="label">Type:</span>
              <span class="value">{{ user.type || 'user' }}</span>
            </div>
            @if (lastUpdated) {
              <div class="info-item">
                <span class="label">Last Updated:</span>
                <span class="value">{{ lastUpdated | date:'short' }}</span>
              </div>
            }
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Discord Information (Users only) -->
      @if (user.type === 'user' && user.connections.discord) {
        <mat-card>
          <mat-card-header>
            <mat-card-title>Discord Information</mat-card-title>
            @if (user.connections.discord.avatar) {
              <app-avatar mat-card-avatar [src]="user.connections.discord.avatar" [alt]="user.connections.discord.username"></app-avatar>
            }
          </mat-card-header>
          <mat-card-content>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Discord ID:</span>
                <span class="value">{{ user.connections.discord.id }}</span>
              </div>
              <div class="info-item">
                <span class="label">Discord Username:</span>
                <span class="value">{{ user.connections.discord.username }}</span>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      }

      <!-- VRChat Information -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>VRChat Information</mat-card-title>
          @if (user.connections.vrchat && user.connections.vrchat.avatar) {
            <app-avatar mat-card-avatar [src]="user.connections.vrchat.avatar" [alt]="user.connections.vrchat.username || 'VRChat Avatar'"></app-avatar>
          }
          <button mat-icon-button (click)="checkVRChatUsername()" [disabled]="checkingVRChat" matTooltip="Check if VRChat username is up-to-date">
            @if (checkingVRChat) {
              <mat-progress-spinner mode="indeterminate" diameter="24"></mat-progress-spinner>
            } @else {
              <mat-icon>refresh</mat-icon>
            }
          </button>
        </mat-card-header>
        <mat-card-content>
          @if (user.connections.vrchat) {
            <div class="info-grid">
              <div class="info-item">
                <span class="label">VRChat ID:</span>
                <a href="https://vrchat.com/home/user/{{ user.connections.vrchat.id }}" target="_blank" class="value">{{ user.connections.vrchat.id }}</a>
              </div>
              <div class="info-item">
                <span class="label">VRChat Username:</span>
                <span class="value">{{ user.connections.vrchat.username }}</span>
              </div>
            </div>

            <!-- VRChat Check Result -->
            @if (vrchatCheckResult) {
              <div class="vrchat-check-result" [class.warning]="!vrchatCheckResult.up_to_date" [class.success]="vrchatCheckResult.up_to_date && vrchatCheckResult.connected">
                @if (!vrchatCheckResult.connected) {
                  <div class="check-message">
                    <mat-icon>error</mat-icon>
                    <span>User is not connected to VRChat</span>
                  </div>
              } @else if (!vrchatCheckResult.up_to_date) {
                  <div class="check-message warning">
                    <mat-icon>warning</mat-icon>
                    <span>
                      <strong>Warning:</strong> VRChat username may be outdated.
                      @if (vrchatCheckResult.stored_username && vrchatCheckResult.current_display_name) {
                        <br>
                        Stored: "{{ vrchatCheckResult.stored_username }}"
                        <br>
                        Current: "{{ vrchatCheckResult.current_display_name }}"
                      }
                    </span>
                    <button mat-raised-button color="primary" (click)="updateVRChatUsername()" [disabled]="updatingVRChat" class="update-button">
                      @if (updatingVRChat) {
                        <mat-progress-spinner mode="indeterminate" diameter="20"></mat-progress-spinner>
                      } @else {
                        Update Username
                      }
                    </button>
                  </div>
                } @else {
                  <div class="check-message success">
                    <mat-icon>check_circle</mat-icon>
                    <span>VRChat username is up-to-date</span>
                  </div>
                }
              </div>
            }
          } @else {
            <div class="osv">
              <mat-icon>info</mat-icon>
              <span>No VRChat connection</span>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <!-- Patreon Information -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>Patreon Information</mat-card-title>
          @if (user.connections.patreon && user.connections.patreon.avatar) {
            <app-avatar mat-card-avatar [src]="user.connections.patreon.avatar" [alt]="user.connections.patreon.username || 'Patreon Avatar'"></app-avatar>
          }
        </mat-card-header>
        <mat-card-content>
          @if (user.connections.patreon) {
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Patreon ID:</span>
                <span class="value">{{ user.connections.patreon.id }}</span>
              </div>
              <div class="info-item">
                <span class="label">Patreon Username:</span>
                <span class="value">{{ user.connections.patreon.username }}</span>
              </div>
              <div class="info-item">
                <span class="label">Subscription Tier:</span>
                <mat-chip [style.background-color]="getUserSubscriptionTier().color">
                  {{ getUserSubscriptionTier().name }}
                </mat-chip>
              </div>
              @if (user.connections.patreon.email) {
                <div class="info-item">
                  <span class="label">Email:</span>
                  <span class="value">{{ user.connections.patreon.email }}</span>
                </div>
              }
              @if (user.connections.patreon.expire) {
                <div class="info-item">
                  <span class="label">Expires:</span>
                  <span class="value">{{ user.connections.patreon.expire }}</span>
                </div>
              }
              @if (user.connections.patreon.connected_at) {
                <div class="info-item">
                  <span class="label">Connected:</span>
                  <span class="value">{{ user.connections.patreon.connected_at }}</span>
                </div>
              }
              @if (user.connections.patreon.overridden) {
                <div class="info-item">
                  <span class="label">Tier Overridden:</span>
                  <mat-chip color="accent">Yes</mat-chip>
                </div>
              }
            </div>
          } @else {
            <div class="no-connection">
              <mat-icon>info</mat-icon>
              <span>No Patreon connection</span>
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>
  }
</div>

